/**
 * lib/subjectChoices.ts — FAMILIES CHOOSE NEXT YEAR'S SUBJECTS (the school,
 * 1 Nov 2026; TIMETABLE-EDITOR-DESIGN.md step 2). Ported from Al-Nur's form
 * (alnur-portal lib/subjectChoices, 30 Oct 2026) and widened for LKC:
 *
 *  - TWO LEVELS. Form 3 families choose IGCSE subjects for Form 4; Form 5
 *    families choose AS subjects. Each level has its own compulsory subjects,
 *    how many to choose (`min`–`max`) and the subjects offered.
 *  - IGCSE: English Language and Mathematics are compulsory and the family
 *    chooses SIX more. Additional Mathematics is ONE OF THE SIX, with no entry
 *    condition (at Al-Nur it is a ninth, gated on a 75% average — not here).
 *  - AS: nothing compulsory; three to five (most pupils took four in 2026).
 *  - WHAT IS OFFERED is what LKC really teaches at that level: the subjects
 *    with marks and comments from real teachers in 2026
 *    (seed/audit-subjects-in-use.cjs, 1 Nov 2026). English as a Second
 *    Language and Mathematics (Core) are tiers of the compulsory subjects the
 *    departments place pupils in, not choices, so they are not offered.
 *  - Cambridge's "Combining with other syllabuses" exceptions REFUSE a pair;
 *    Further Mathematics is refused without Mathematics; pairs that overlap a
 *    lot are a gentle note, never a refusal.
 *  - The window opens with `open` and closes at the end of `closesOn`
 *    (Gaborone). The school set 30 Nov 2026.
 *
 * Subjects are the portal's own subject ids (reportSubjects), so the choices
 * feed staffing and the timetable without translation. Defaults are written
 * as NAMES and resolved against the portal's subjects (`defaultSettings`),
 * because the ids differ between projects. Pure: no Firebase.
 * ------------------------------------------------------------------ */

export type Level = 'igcse' | 'as';
export const LEVELS: Level[] = ['igcse', 'as'];
export const LEVEL_LABEL: Record<Level, string> = { igcse: 'IGCSE', as: 'AS Level' };

export interface LevelSettings {
  /** Pupils whose class name starts with this choose ("Form 3", "Form 5"). */
  forClasses: string;
  /** What they are choosing for, for the parent ("Form 4", "AS"). */
  forLabel: string;
  /** Portal subject ids everyone at this level takes. */
  compulsory: string[];
  /** Portal subject ids the family chooses from. */
  offered: string[];
  /** How many the family chooses besides the compulsory ones. */
  min: number;
  max: number;
}

export interface SubjectChoiceSettings {
  open: boolean;
  /** The year the choices are for, e.g. 2027. */
  year: number;
  /** Last day choices may be made or changed, YYYY-MM-DD (Gaborone). '' = no date. */
  closesOn: string;
  /** End of that day as epoch ms, which the rules compare with request.time. */
  closesAt: number;
  igcse: LevelSettings;
  as: LevelSettings;
}

export interface SubjectInfo {
  covers: string;
  whoFor: string;
  careers: string[];
}

export interface SubjectInfoEdit extends Partial<SubjectInfo> {
  updatedAt?: string;
  updatedBy?: string;
  updatedByName?: string;
}

export interface SubjectChoiceDoc {
  year: number;
  level: Level;
  studentId: string;
  studentName: string;
  className: string;
  /** Portal subject ids chosen (the compulsory ones are not repeated here). */
  subjects: string[];
  submittedBy: string;
  submittedByName: string;
  updatedAt: string;
}

export const SETTINGS_DOC = 'subjectChoices';
export const CHOICES = 'reportSubjectChoices';
export const INFO = 'reportSubjectInfo';

export const choiceId = (year: number, studentId: string) => `${year}__${studentId}`;
/** A description is per level: IGCSE Biology and AS Biology are different courses. */
export const infoId = (level: Level, subjectId: string) => `${level}__${subjectId}`;

/* ── Cambridge codes, by the portal's subject names ────────────────── */

const norm = (s: string) => s.toLowerCase().replace(/&/g, 'and').replace(/[^a-z]+/g, ' ').trim();

const CODES: Record<Level, Record<string, string>> = {
  igcse: {
    'english language': '0500', 'mathematics': '0580', 'accounting': '0452', 'add maths': '0606',
    'additional mathematics': '0606', 'agriculture': '0600', 'art and design': '0400', 'biology': '0610',
    'business studies': '0450', 'chemistry': '0620', 'computer science': '0478', 'design and technology': '0445',
    'economics': '0455', 'english literature': '0475', 'enterprise': '0454', 'french': '0520', 'geography': '0460',
    'global perspectives': '0457', 'history': '0470', 'physical education': '0413', 'physics': '0625',
    'setswana': '0698', 'travel and tourism': '0471', 'english second language': '0510', 'ict': '0417',
  },
  as: {
    'accounting': '9706', 'biology': '9700', 'business studies': '9609', 'chemistry': '9701',
    'computer science': '9618', 'design and technology': '9705', 'economics': '9708', 'english language': '9093',
    'english literature': '9695', 'further mathematics': '9231', 'geography': '9696', 'history': '9489',
    'mathematics': '9709', 'physics': '9702', 'psychology': '9990', 'physical education': '9396',
  },
};

export function cambridgeCode(level: Level, subjectName: string): string {
  return CODES[level][norm(subjectName)] ?? '';
}

/* ── defaults ─────────────────────────────────────────────────────── */

/** The subjects LKC taught at each level in 2026, with real marks and comments. */
export const DEFAULT_NAMES: Record<Level, { compulsory: string[]; offered: string[] }> = {
  igcse: {
    compulsory: ['English Language', 'Mathematics'],
    offered: [
      'Accounting', 'Add Maths', 'Agriculture', 'Art & Design', 'Biology', 'Business Studies', 'Chemistry',
      'Computer Science', 'Design & Technology', 'Economics', 'English Literature', 'Enterprise', 'French',
      'Geography', 'Global Perspectives', 'History', 'Physical Education', 'Physics', 'Setswana', 'Travel and Tourism',
    ],
  },
  as: {
    compulsory: [],
    offered: [
      'Accounting', 'Biology', 'Business Studies', 'Chemistry', 'Computer Science', 'Design & Technology',
      'Economics', 'English Language', 'English Literature', 'Further Mathematics', 'Geography', 'History',
      'Mathematics', 'Physical Education', 'Physics', 'Psychology',
    ],
  },
};

type SubjectLike = { id: string; name: string };

/** Names → the portal's ids; a name the portal does not have is dropped. */
export function idsFor(names: string[], subjects: SubjectLike[]): string[] {
  const by = new Map(subjects.map((s) => [norm(s.name), s.id]));
  return names.map((n) => by.get(norm(n))).filter((x): x is string => !!x);
}

export function defaultSettings(subjects: SubjectLike[], year = new Date().getFullYear() + 1): SubjectChoiceSettings {
  return {
    open: false,
    year,
    closesOn: '',
    closesAt: 0,
    igcse: {
      forClasses: 'Form 3', forLabel: 'Form 4', min: 6, max: 6,
      compulsory: idsFor(DEFAULT_NAMES.igcse.compulsory, subjects), offered: idsFor(DEFAULT_NAMES.igcse.offered, subjects),
    },
    as: {
      forClasses: 'Form 5', forLabel: 'AS', min: 3, max: 5,
      compulsory: idsFor(DEFAULT_NAMES.as.compulsory, subjects), offered: idsFor(DEFAULT_NAMES.as.offered, subjects),
    },
  };
}

export function settingsFrom(raw: Partial<SubjectChoiceSettings> | undefined | null, subjects: SubjectLike[]): SubjectChoiceSettings {
  const d = defaultSettings(subjects);
  if (!raw) return d;
  return { ...d, ...raw, igcse: { ...d.igcse, ...(raw.igcse ?? {}) }, as: { ...d.as, ...(raw.as ?? {}) } };
}

/** End of a Gaborone (UTC+2) day as epoch ms; 0 for no date. */
export function closesAtFor(closesOn: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(closesOn)) return 0;
  return Date.parse(`${closesOn}T23:59:59+02:00`);
}

/** Can a family choose or change now? */
export function isOpen(s: SubjectChoiceSettings, nowMs: number): boolean {
  return s.open && (!s.closesAt || nowMs <= s.closesAt);
}

/** Which level a pupil in this class chooses for, if any. */
export function levelForClass(s: SubjectChoiceSettings, className: string): Level | null {
  for (const l of LEVELS) {
    const p = s[l].forClasses.trim();
    if (p && (className === p || className.startsWith(`${p} `))) return l;
  }
  return null;
}

/* ── descriptions ─────────────────────────────────────────────────── */

/** Drafts for the departments to correct, by subject name. */
const DRAFTS: Record<string, SubjectInfo> = {
  'english language': { covers: 'Reading and writing in English for different purposes and audiences.', whoFor: 'Everyone.', careers: ['Every career'] },
  'mathematics': { covers: 'Number, algebra, geometry, trigonometry, statistics and probability.', whoFor: 'Everyone.', careers: ['Every career'] },
  'add maths': { covers: 'Algebra, functions, trigonometry and calculus beyond IGCSE Mathematics.', whoFor: 'Strong mathematicians planning AS Mathematics, the sciences or engineering.', careers: ['Engineering', 'Actuarial science', 'Data science', 'Physics'] },
  'further mathematics': { covers: 'Further pure mathematics, mechanics and statistics beyond A Level Mathematics.', whoFor: 'The strongest mathematicians, taking Mathematics alongside it.', careers: ['Engineering', 'Actuarial science', 'Physics', 'Computer science'] },
  'physics': { covers: 'Forces and motion, energy, waves, electricity and magnetism, and the atom.', whoFor: 'Pupils who like working out how things work and are comfortable with maths.', careers: ['Engineering', 'Medicine and radiography', 'Mining', 'Aviation', 'Architecture'] },
  'chemistry': { covers: 'What substances are made of, how they react, and organic chemistry, with practical work.', whoFor: 'Pupils who enjoy careful practical work and are steady with numbers.', careers: ['Medicine', 'Pharmacy', 'Nursing', 'Chemical engineering', 'Metallurgy'] },
  'biology': { covers: 'Cells, the human body, plants, ecology and genetics.', whoFor: 'Pupils interested in living things, health and the environment.', careers: ['Medicine and nursing', 'Dentistry', 'Veterinary science', 'Agriculture', 'Wildlife conservation'] },
  'accounting': { covers: 'Recording transactions, books of account and financial statements.', whoFor: 'Pupils who are organised and accurate with figures.', careers: ['Accountant', 'Auditor', 'Banking', 'Running a business'] },
  'business studies': { covers: 'How businesses start, are organised, market products, employ people and manage money.', whoFor: 'Pupils interested in enterprise and how organisations work.', careers: ['Management', 'Entrepreneurship', 'Marketing', 'Human resources'] },
  'economics': { covers: 'Markets, prices, government policy, development and international trade.', whoFor: 'Pupils who enjoy reasoning, current affairs and writing arguments.', careers: ['Economist', 'Banking and finance', 'Public policy', 'Law'] },
  'enterprise': { covers: 'Planning and running an enterprise project: ideas, markets, money and people.', whoFor: 'Pupils who want to start something of their own.', careers: ['Entrepreneurship', 'Management', 'Marketing'] },
  'computer science': { covers: 'Programming, algorithms, data and how computers and networks work.', whoFor: 'Logical pupils who enjoy solving problems step by step.', careers: ['Software development', 'Cyber security', 'Data science', 'Engineering'] },
  'geography': { covers: 'Population and settlement, natural hazards, rivers, coasts, weather, development and fieldwork.', whoFor: 'Pupils curious about places, people and the environment.', careers: ['Town planning', 'Environmental science', 'Surveying', 'Tourism'] },
  'history': { covers: 'International relations in the twentieth century and a depth study.', whoFor: 'Pupils who enjoy reading, weighing evidence and writing.', careers: ['Law', 'Journalism', 'Diplomacy', 'Teaching'] },
  'travel and tourism': { covers: 'The travel and tourism industry, customer service, destinations and marketing.', whoFor: 'Pupils interested in Botswana’s tourism and hospitality sectors.', careers: ['Tourism', 'Hospitality', 'Aviation', 'Events'] },
  'agriculture': { covers: 'Soils, crops, livestock, farm management and practical agriculture.', whoFor: 'Pupils interested in farming, food and the land.', careers: ['Farming', 'Agricultural science', 'Veterinary work', 'Agribusiness'] },
  'art and design': { covers: 'Drawing, painting, design and making, with a coursework portfolio.', whoFor: 'Creative pupils who like to draw, design and make.', careers: ['Graphic design', 'Architecture', 'Fashion', 'Media'] },
  'design and technology': { covers: 'Designing and making products, with a design project.', whoFor: 'Practical pupils who like drawing, designing and making.', careers: ['Engineering', 'Architecture', 'Product design', 'Construction'] },
  'english literature': { covers: 'Poetry, prose and drama set texts, and unseen passages.', whoFor: 'Keen readers who enjoy discussing and writing about texts.', careers: ['Law', 'Journalism', 'Teaching', 'Media'] },
  'french': { covers: 'Listening, reading, speaking and writing in French.', whoFor: 'Pupils who enjoy languages and want an international option.', careers: ['Diplomacy', 'Tourism', 'International business', 'Translation'] },
  'setswana': { covers: 'Setswana language and its set literature.', whoFor: 'Pupils who speak Setswana at home or are fluent in it.', careers: ['Teaching', 'Media and broadcasting', 'Public service', 'Law'] },
  'global perspectives': { covers: 'Researching global issues from different viewpoints, with a team project.', whoFor: 'Pupils who like to research, discuss and argue a case.', careers: ['Law', 'Development work', 'Journalism', 'Diplomacy'] },
  'physical education': { covers: 'The science of sport and exercise, with practical performance.', whoFor: 'Sporty pupils interested in how the body performs.', careers: ['Sports science', 'Physiotherapy', 'Coaching', 'Teaching'] },
  'psychology': { covers: 'How people think, feel and behave, through classic studies and research methods.', whoFor: 'Pupils curious about people and comfortable with data.', careers: ['Psychology', 'Counselling', 'Human resources', 'Medicine'] },
};

/** A subject's description: the head of department's edit over the draft. */
export function infoFor(subjectName: string, edit?: SubjectInfoEdit): SubjectInfo {
  const base = DRAFTS[norm(subjectName)] ?? { covers: '', whoFor: '', careers: [] };
  const e = edit ?? {};
  return {
    covers: e.covers?.trim() || base.covers,
    whoFor: e.whoFor?.trim() || base.whoFor,
    careers: Array.isArray(e.careers) && e.careers.length ? e.careers : base.careers,
  };
}

/* ── checks ───────────────────────────────────────────────────────── */

/** Cambridge's exceptions per code (only the ones that can matter here). */
export const CAMBRIDGE_EXCLUDES: Record<string, string[]> = {
  '0500': ['0472', '0510', '0511', '0990'],
  '0580': ['0607', '0980'],
  '0620': ['0971', '0652', '0653', '0654'],
  '0610': ['0970', '0653', '0654'],
  '0625': ['0972', '0652', '0653', '0654'],
  '0478': ['0265', '0984'],
  '0475': ['0408'],
  '0520': ['0501'],
};

/** Pairs Cambridge will not enter together, as codes. */
export function cambridgeClashes(codes: Iterable<string>): [string, string][] {
  const list = [...new Set(codes)].filter(Boolean).sort();
  const out: [string, string][] = [];
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const [a, b] = [list[i], list[j]];
      if ((CAMBRIDGE_EXCLUDES[a] ?? []).includes(b) || (CAMBRIDGE_EXCLUDES[b] ?? []).includes(a)) out.push([a, b]);
    }
  }
  return out;
}

/** Pairs that share a lot of content: allowed, with a gentle note (by name). */
const OVERLAPS: { pair: [string, string]; note: string }[] = [
  { pair: ['business studies', 'economics'], note: 'Business Studies and Economics overlap on markets, prices and government policy.' },
  { pair: ['business studies', 'accounting'], note: 'Business Studies and Accounting overlap on business finance and financial statements.' },
  { pair: ['business studies', 'enterprise'], note: 'Business Studies and Enterprise overlap a good deal. Many pupils take one.' },
];

export interface ChoiceCheck {
  /** Reasons the choices cannot be sent. */
  blocking: string[];
  /** Gentle notes; the choices can still be sent. */
  notes: string[];
}

export function checkChoices(
  s: SubjectChoiceSettings,
  level: Level,
  picked: string[],
  nameOf: (id: string) => string,
): ChoiceCheck {
  const L = s[level];
  const blocking: string[] = [];
  const chosen = [...new Set(picked)].filter((x) => !L.compulsory.includes(x));
  if (chosen.length < L.min || chosen.length > L.max) {
    blocking.push(L.min === L.max
      ? `Choose ${L.min} subjects (${chosen.length} chosen).`
      : `Choose ${L.min} to ${L.max} subjects (${chosen.length} chosen).`);
  }
  const unknown = chosen.filter((c) => !L.offered.includes(c));
  if (unknown.length) blocking.push(`${unknown.map(nameOf).join(', ')} is not offered.`);
  const all = [...L.compulsory, ...chosen];
  const names = all.map((id) => norm(nameOf(id)));
  const byCode = new Map(all.map((id) => [cambridgeCode(level, nameOf(id)), id]));
  for (const [a, b] of cambridgeClashes(byCode.keys())) {
    blocking.push(`Cambridge does not allow ${nameOf(byCode.get(a)!)} and ${nameOf(byCode.get(b)!)} in the same exam series.`);
  }
  if (names.includes('further mathematics') && !names.includes('mathematics')) {
    blocking.push('Further Mathematics is taken alongside Mathematics: choose Mathematics too.');
  }
  const notes = OVERLAPS.filter((o) => names.includes(o.pair[0]) && names.includes(o.pair[1])).map((o) => o.note);
  return { blocking, notes };
}

/** How many families chose each subject. */
export function countBySubject(choices: Pick<SubjectChoiceDoc, 'subjects'>[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const c of choices) for (const s of new Set(c.subjects)) m.set(s, (m.get(s) ?? 0) + 1);
  return m;
}
